import { Box, Button, Icon, styled } from "@outschool/backpack";
import { faChevronLeft, faChevronRight } from "@outschool/icons";
import { useIsMobile } from "@outschool/ui-utils";
import React, { useEffect } from "react";

enum Direction {
  Left,
  Right,
}

const Carousel = styled(Box)({
  position: "relative",
});

const CarouselContent = styled(Box)({
  display: "flex",
  gap: "1rem",
  overflow: "auto",
});

function useCarouselScroll(ref: React.RefObject<HTMLElement>) {
  const [hasMoreToTheLeft, setHasMoreToTheLeft] = React.useState(false);
  const [hasMoreToTheRight, setHasMoreToTheRight] = React.useState(false);

  useEffect(() => {
    if (ref.current === null) {
      return;
    }
    let element = ref.current;

    function scrollHandler() {
      setHasMoreToTheLeft(element.scrollLeft > 0);
      setHasMoreToTheRight(
        element.scrollLeft + element.clientWidth < element.scrollWidth,
      );
    }

    element.addEventListener("scroll", scrollHandler);

    scrollHandler();

    return () => {
      element.removeEventListener("scroll", scrollHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = (direction: Direction) => {
    if (ref.current === null) {
      return;
    }

    const left = ref.current.scrollLeft;
    const width = ref.current.getBoundingClientRect().width;

    ref.current.scrollTo({
      left: direction === Direction.Left ? left - width : left + width,
      behavior: "smooth",
    });
  };

  return {
    hasMoreToTheLeft,
    hasMoreToTheRight,
    onScroll: handleScroll,
  };
}

function EdgeButton({
  onClick,
  direction,
}: {
  onClick: (direction: Direction) => void;
  direction: Direction;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        top: 0,
        left: direction === Direction.Left ? 0 : "auto",
        right: direction === Direction.Right ? 0 : "auto",
        zIndex: 2,
        border: "none",
        background: "white",
        maskImage:
          direction === Direction.Left
            ? "linear-gradient(to left, transparent 0%, black 2rem);"
            : "linear-gradient(to right, transparent 0%, black 2rem);",
        height: "100%",
        padding:
          direction === Direction.Left
            ? "1rem 2rem 1rem 1rem"
            : "1rem 1rem 1rem 2rem",
      }}
    >
      <Button
        onClick={() => onClick(direction)}
        sx={{
          p: 0,
          height: "3.65rem",
          width: "3.65rem",
        }}
      >
        <Icon
          icon={direction === Direction.Left ? faChevronLeft : faChevronRight}
        />
      </Button>
    </Box>
  );
}

function DesktopTeacherCarousel({ children }: Props) {
  const ref = React.useRef<HTMLElement>(null);
  const { hasMoreToTheLeft, hasMoreToTheRight, onScroll } =
    useCarouselScroll(ref);

  return (
    <Carousel sx={{ mx: "1em" }}>
      {hasMoreToTheLeft && (
        <EdgeButton onClick={onScroll} direction={Direction.Left} />
      )}

      <CarouselContent ref={ref}>{children}</CarouselContent>

      {hasMoreToTheRight && (
        <EdgeButton onClick={onScroll} direction={Direction.Right} />
      )}
    </Carousel>
  );
}

function MobileTeacherCarousel({ children }: Props) {
  return (
    <Carousel>
      <CarouselContent
        sx={{
          px: "1em",
        }}
      >
        {children}
      </CarouselContent>
    </Carousel>
  );
}

type Props = {
  children: React.ReactNode;
};

export function TeacherCarousel(props: Props) {
  const isMobile = useIsMobile();

  const Component = isMobile ? MobileTeacherCarousel : DesktopTeacherCarousel;

  return <Component {...props} />;
}
